import React, { useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';

/**
 * FileField — reusable file upload input
 *
 * Props:
 * - label (string): field label text
 * - registration (object): react-hook-form registration object
 * - error (object): validation error from react-hook-form
 * - multiple (boolean): allow multiple file selection
 * - accept (string): allowed MIME types (e.g. "image/*,video/*")
 * - helperText (string): optional helper text below input
 */

export const FileField = ({ label, registration, error, multiple = false, accept, helperText }) => {
  const [fileNames, setFileNames] = useState([]);

  const handleChange = (e) => {
    const files = Array.from(e.target.files || []);
    const names = files.map((f) => f.name);
    setFileNames(names);
  };

  return (
    <div className="w-full flex flex-col space-y-1.5">
      {label && <label className="block text-sm font-medium text-gray-700">{label}</label>}

      <input
        type="file"
        className={clsx(
          'block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500',
          error && 'border-red-500 focus:ring-red-500 focus:border-red-500'
        )}
        {...registration}
        multiple={multiple}
        accept={accept}
        onChange={(e) => {
          handleChange(e);
          registration?.onChange?.(e); // ensure form state updates too
        }}
      />

      {/* File name preview */}
      {fileNames.length > 0 && (
        <div className="text-xs text-gray-500 mt-1">
          {fileNames.length === 1
            ? `Selected: ${fileNames[0]}`
            : `${fileNames.length} files selected`}
        </div>
      )}

      {/* Optional helper text */}
      {helperText && <p className="text-xs text-gray-400">{helperText}</p>}

      {/* Error message */}
      {error && (
        <p className="text-xs text-red-600 mt-1">{error.message || 'Invalid file input'}</p>
      )}
    </div>
  );
};

FileField.propTypes = {
  label: PropTypes.string,
  registration: PropTypes.object,
  error: PropTypes.object,
  multiple: PropTypes.bool,
  accept: PropTypes.string,
  helperText: PropTypes.string,
};
